import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, payOrder } from '../../services/api';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';
import useToast from '../../hooks/useToast';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';

const OrderQR = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // ✅ React Query: Fetch order details
  const {
    data: order,
    isLoading,
    isError,
    refetch: refetchOrder,
  } = useApiQuery(
    ['order', id],
    () => getOrder(id).then(res => res.data),
    {
      staleTime: 30 * 1000, // 30 seconds
      onError: () => {
        showError('Order not found');
        navigate('/vendor/orders');
      },
    }
  );

  // ✅ React Query: Pay order mutation
  const payOrderMutation = useApiMutation(
    () => payOrder(id).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Payment completed');
        navigate('/vendor/orders');
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Payment failed');
      },
    }
  );

  // ✅ Handle Pay Now
  const handlePayNow = useCallback(() => {
    payOrderMutation.mutate();
  }, [payOrderMutation]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading order...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isError || !order) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <p className="text-red-500">Order not found</p>
        <button
          onClick={() => navigate('/vendor/orders')}
          className="mt-4 text-[#1471b9] hover:underline"
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const orderUrl = `${window.location.origin}/scan/order/${order._id}`;

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-[15px] border border-[#dae1e7] p-6 space-y-4">
      <h2 className="text-xl font-semibold text-[#2b3445]">
        Order #{order._id.slice(-6)}
      </h2>
      
      <div className="grid grid-cols-2 gap-2 text-sm">
        <span className="text-[#708090]">Attendee:</span>
        <span>{order.attendeeId?.name || order.attendeeId}</span>
        
        <span className="text-[#708090]">Total:</span>
        <span className="font-bold">₹{order.total.toFixed(2)}</span>
        
        <span className="text-[#708090]">Status:</span>
        <span className={`font-medium ${
          order.status === 'paid' ? 'text-green-600' : 'text-yellow-600'
        }`}>
          {order.status}
        </span>
        
        <span className="text-[#708090]">Items:</span>
        <span>
          {order.items.map(i => `${i.foodItemId?.name || 'item'} x${i.quantity}`).join(', ')}
        </span>
      </div>

      <div className="flex flex-col items-center py-4 border-t border-[#dae1e7]">
        <h3 className="text-sm font-semibold text-[#2b3445] mb-2">Scan to Pay</h3>
        <QRCodeSVG value={orderUrl} size={200} />
        <p className="text-xs text-[#708090] mt-2">
          Scan this QR with the scanner device to complete payment
        </p>
      </div>

      {order.status === 'pending' && (
        <button
          onClick={handlePayNow}
          disabled={payOrderMutation.isPending}
          className="w-full bg-green-600 text-white px-4 py-2 rounded-[8px] font-bold hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {payOrderMutation.isPending ? 'Processing...' : 'Pay Now (Manual)'}
        </button>
      )}

      {order.status === 'paid' && (
        <div className="w-full bg-green-50 text-green-700 px-4 py-2 rounded-[8px] font-bold text-center">
          ✅ Order Already Paid
        </div>
      )}

      <button
        onClick={() => navigate('/vendor/orders')}
        className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-[8px] font-bold hover:bg-gray-300 transition-colors"
      >
        Back to Orders
      </button>
    </div>
  );
};

export default OrderQR;